import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import { 
  Leaf, 
  Target,
  Users,
  Activity,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Shield,
  Recycle,
  Heart,
  AlertTriangle,
  Globe
} from 'lucide-react';

const EnvironnementPage = () => {
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const objectives = [
    'environnement.program.objectives.obj1',
    'environnement.program.objectives.obj2',
    'environnement.program.objectives.obj3',
    'environnement.program.objectives.obj4',
    'environnement.program.objectives.obj5'
  ];

  const activityCategories = [
    {
      icon: Shield,
      titleKey: 'environnement.program.activities.cat1.title',
      items: [
        'environnement.program.activities.cat1.item1',
        'environnement.program.activities.cat1.item2',
        'environnement.program.activities.cat1.item3'
      ]
    },
    {
      icon: Recycle,
      titleKey: 'environnement.program.activities.cat2.title',
      items: [
        'environnement.program.activities.cat2.item1',
        'environnement.program.activities.cat2.item2',
        'environnement.program.activities.cat2.item3'
      ]
    },
    {
      icon: Heart,
      titleKey: 'environnement.program.activities.cat3.title',
      items: [
        'environnement.program.activities.cat3.item1',
        'environnement.program.activities.cat3.item2',
        'environnement.program.activities.cat3.item3'
      ]
    },
    {
      icon: AlertTriangle,
      titleKey: 'environnement.program.activities.cat4.title',
      items: [
        'environnement.program.activities.cat4.item1',
        'environnement.program.activities.cat4.item2',
        'environnement.program.activities.cat4.item3'
      ]
    },
    {
      icon: Globe,
      titleKey: 'environnement.program.activities.cat5.title',
      items: [
        'environnement.program.activities.cat5.item1',
        'environnement.program.activities.cat5.item2'
      ]
    }
  ];

  const impacts = [
    'environnement.program.impact.impact1',
    'environnement.program.impact.impact2',
    'environnement.program.impact.impact3',
    'environnement.program.impact.impact4',
    'environnement.program.impact.impact5'
  ];

  const partners = [
    'environnement.program.partnerships.partner1',
    'environnement.program.partnerships.partner2',
    'environnement.program.partnerships.partner3',
    'environnement.program.partnerships.partner4'
  ];

  const contributions = [
    'environnement.program.alignment.contrib1',
    'environnement.program.alignment.contrib2',
    'environnement.program.alignment.contrib3',
    'environnement.program.alignment.contrib4'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Compact */}
      <section className="relative h-[280px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#22c55e] via-[#089556] to-[#0ba8c7]">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px)`,
              backgroundSize: '100px 100px'
            }}></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center relative z-10 max-w-4xl mx-auto px-4">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                  <Leaf className="h-4 w-4 text-white" />
                </div>
                <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">
                  {t('environnement.program.hero.subtitle')}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-3xl font-bold text-white mb-3 leading-tight">
                {t('environnement.program.hero.title')}
              </h1>
              <p className="text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed">
                {t('environnement.program.hero.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Context & Objectives - Side by Side with Image */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Image Column */}
          <div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/projet/environnement/Image1.jpg"
                alt="Programme environnement"
                className="w-full h-[400px] object-cover"
                loading="eager"
              />
            </div>
          </div>
          
          {/* Content Column */}
          <div className="space-y-6">
            {/* Context */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#22c55e] rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                {t('environnement.program.context.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed text-[15px]">
                {t('environnement.program.context.content')}
              </p>
            </div>

            {/* Objectives */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#089556] rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                {t('environnement.program.objectives.title')}
              </h2>
              <p className="text-sm text-gray-600 mb-3 italic">
                {t('environnement.program.objectives.general')}
              </p>
              <ul className="space-y-2">
                {objectives.map((objKey, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t(objKey)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Activities - Compact Grid */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center flex items-center justify-center gap-2">
            <Activity className="w-7 h-7 text-[#22c55e]" />
            {t('environnement.program.activities.title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {activityCategories.map((category, idx) => {
              const Icon = category.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#22c55e] to-[#089556] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 pt-2">
                      {t(category.titleKey)}
                    </h3>
                  </div>
                  <ul className="space-y-2 ml-16">
                    {category.items.map((itemKey, itemIdx) => (
                      <li key={itemIdx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{t(itemKey)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact - With Image */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#22c55e]" />
              {t('environnement.program.impact.title')}
            </h2>
            <div className="space-y-3">
              {impacts.map((impactKey, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700 leading-snug">
                    {t(impactKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/projet/environnement/Image2.jpg"
                alt="Impact du programme"
                className="w-full h-64 object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partnerships & Alignment */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Partnerships */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('environnement.program.partnerships.title')}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('environnement.program.partnerships.intro')}
              </p>
              <div className="space-y-2">
                {partners.map((partnerKey, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                    <CheckCircle className="w-4 h-4 text-[#22c55e] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{t(partnerKey)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* International Alignment */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('environnement.program.alignment.title')}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('environnement.program.alignment.intro')}
              </p>
              <div className="space-y-2 mb-4">
                {contributions.map((contribKey, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                    <CheckCircle className="w-4 h-4 text-[#089556] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{t(contribKey)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-br from-[#22c55e] to-[#089556] rounded-xl p-4 text-white">
                <p className="text-sm text-white/95 leading-relaxed">
                  {t('environnement.program.alignment.odd')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Section - Third Image */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="rounded-xl overflow-hidden shadow-xl">
          <img 
            src="/projet/environnement/Image3.jpg"
            alt="Activités de sensibilisation"
            className="w-full h-96 object-cover"
            loading="lazy"
          />
        </div>
      </section>

      {/* CTA - Compact */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-gradient-to-r from-[#22c55e] to-[#089556] rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            {t('environnement.program.cta.title')}
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            {t('environnement.program.cta.description')}
          </p>
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              navigate(`/${currentLanguage}/contact`);
            }}
            className="inline-flex items-center gap-2 bg-white text-[#22c55e] px-6 py-3 rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            {t('environnement.program.cta.button')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnvironnementPage;
